import { useEffect, useRef } from "react";
import { useAction, useMutation } from "convex/react";
import { useSetAtom } from "jotai";

import { api } from "../../../convex/_generated/api";
import { bootstrapStatusAtom } from "@/core/state";
import { useSession } from "./useSession";

// Au premier render après login :
//   1. Mutation `syncSelfFromWorkOS` — upsert user Convex (mapping
//      workosUserId → email/name/avatar). Pas dépendant de l'API WorkOS.
//   2. Action `bootstrapFromWorkOS` — fetch les org memberships de cet
//      user via WorkOS API et crée account+membership Convex pour chaque.
//      Self-heal pour le cas où le webhook WorkOS n'est pas configuré ou
//      en retard. Idempotent (les mutations underlying upsert).
//
// Le statut est publié sur `bootstrapStatusAtom` pour que l'OrgGate puisse
// distinguer "bootstrap en cours" (loader) vs "bootstrap fini, 0 account"
// (onboarding screen).
//
// Un guard `bootstrappedRef` évite de re-call l'action à chaque re-render
// (l'action coûte un round-trip WorkOS), tant que le user ne change pas.
export function UserSync() {
  const { user } = useSession();
  const syncSelf = useMutation(api.users.syncSelfFromWorkOS);
  const bootstrap = useAction(api.users.bootstrapFromWorkOS);
  const setBootstrapStatus = useSetAtom(bootstrapStatusAtom);
  const bootstrappedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      bootstrappedRef.current = null;
      setBootstrapStatus("idle");
      return;
    }
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    const args = {
      email: user.email,
      name: name.length > 0 ? name : undefined,
      avatarUrl: user.profilePictureUrl ?? undefined,
    };
    void syncSelf(args);

    // Bootstrap memberships une seule fois par session user (workosUserId).
    // Si une autre invitation arrive en cours de session, l'admin peut
    // forcer un refresh manuel (la matrice + sidebar relisent live).
    if (bootstrappedRef.current === user.workosUserId) return;
    bootstrappedRef.current = user.workosUserId;
    setBootstrapStatus("running");
    bootstrap(args)
      .then(() => setBootstrapStatus("done"))
      .catch((err) => {
        console.error("[UserSync] bootstrap error:", err);
        setBootstrapStatus("error");
        bootstrappedRef.current = null; // permet retry au prochain render
      });
  }, [user, syncSelf, bootstrap, setBootstrapStatus]);

  return null;
}
